import { BaseSensorWebView } from '../../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { SensorType } from '../../../types/sensor-type';
import _ from 'lodash';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../../editIntegration/EditIntegrationConstants';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { GzInputErrorWebComponent } from '../../../../../ui/gzForm/inputError';
import { Sensor } from '../../../../common/types';
import { WEB_COMPONENT_GZ_LABELED_CONTENT, WEB_COMPONENT_GZ_TOOLTIP } from '../../../../../webcomponents.constants';
import { FontSizes } from '../../../../../types/common/types';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';

export class AtlassianAuditView extends BaseSensorWebView {
  public formGroup: FormGroup;

  public dom: {
    $responseActions: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $organizationId: GzFormFieldWebComponent;
    $adminApiKey: GzFormFieldWebComponent;
    $inputError: GzInputErrorWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.ATLASSIAN_AUDIT}">
          <gz-title class="sensor-title">${dependencies.translateFn(
            'Before you integrate the Atlassian Cloud platform with GravityZone XDR, make sure the following requirements are met:'
          )}
          </gz-title>
          </b><br><br>
          <gz-title class="sensor-title">${dependencies.translateFn('For the Atlassian Admin sensor setup')}
          </gz-title>
          <ol>
            <li>${dependencies.translateFn('Note your {organizationLink}.', {
              organizationLink:
                '<gz-link type="underline" href="https://confluence.atlassian.com/jirakb/what-it-is-the-organization-id-and-where-to-find-it-1207189876.html"><span>' +
                dependencies.translateFn('Atlassian organization ID') +
                '</span></gz-link>',
            })}
            </li>
            <li>${dependencies.translateFn('Generate and note the {adminApiKeyLink} to use for the integration.', {
              adminApiKeyLink:
                '<gz-link type="underline" href="https://support.atlassian.com/organization-administration/docs/manage-an-organization-with-the-admin-apis/"><span>' +
                dependencies.translateFn('Atlassian Admin API key') +
                '</span></gz-link>',
            })}
            </li>
          </ol>
          <p>${dependencies.translateFn(
            'To enable communication between the sensor and the Atlassian Cloud platform, an Atlassian Guard subscription is required for your organization.'
          )}
          </p>
          </b><br>
          <gz-title class="sensor-title">${dependencies.translateFn('For the Jira and Confluence sensor integrations')}
          </gz-title>
          <ol>
            <li>${dependencies.translateFn('Note the site domain of your Atlassian product instance.')}
            </li>
            <li>${dependencies.translateFn('Note the email address of the administrator account of the product.')}
            </li>
            <li>${dependencies.translateFn(
              'Generate and note the {apiTokenLink} for the administrator account of the product.',
              {
                apiTokenLink:
                  '<gz-link type="underline" href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"><span>' +
                  dependencies.translateFn('API token') +
                  '</span></gz-link>',
              }
            )}
            </li>
          </ol>
          <p>${dependencies.translateFn(
            'These prerequisites apply to each Atlassian product instance you want to integrate.'
          )}
          </p>
          <p>${dependencies.translateFn(
            'For a complete Atlassian Cloud sensor integration guide, refer to {detailLink}.',
            {
              detailLink:
                '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77209-934132-the-atlassian-cloud-sensor.html"><span>' +
                dependencies.translateFn('this KB article') +
                '</span></gz-link>',
            }
          )}
          </p>
        </div>
        `;
  }

  public static getSensorSpecifics(sensor: Sensor, dependencies: any): string {
    if (!sensor.specifics.orgId) {
      return ``;
    }

    return `
      <${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${dependencies.translateFn('Organization ID')}" hasColon="true">
        <${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${FontSizes.PARAGRAPH_12}" tooltipText="${sensor.specifics.orgId}">
          ${sensor.specifics.orgId}
        </${WEB_COMPONENT_GZ_TOOLTIP}>
      </${WEB_COMPONENT_GZ_LABELED_CONTENT}>
      `;
  }

  protected createFormGroup(): void {
    this.dom.$organizationId.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.OrganizationId.MaxLength,
        this.dependencies.translateFn('The organization ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.OrganizationId.MaxLength,
        })
      ),
    ];
    this.dom.$adminApiKey.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.AdminApiKey.MaxLength,
        this.dependencies.translateFn('The admin API key must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.AdminApiKey.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The API token does not support spaces.')),
    ];

    this.formGroup = new FormGroup({
      org_id: this.dom.$organizationId,
      api_token: this.dom.$adminApiKey,
      responseActions: this.dom.$responseActions,
    });
  }

  public getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $responseActions: this.shadow.querySelector('#response_actions_form'),
      $organizationId: this.shadow.querySelector('#atlassian_organization_id'),
      $adminApiKey: this.shadow.querySelector('#atlassian_admin_api_key'),
      $inputError: this.shadow.querySelector('#response_actions_error'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  public setTranslatedStrings(): void {
    this.dom.$responseActions.Input.label = this.dependencies.translateFn(
      'Allow response actions from GravityZone Incidents'
    );
    this.dom.$organizationId.label = this.dependencies.translateFn('Atlassian organization ID');
    this.dom.$adminApiKey.label = this.dependencies.translateFn('Atlassian Admin API key');
  }

  protected async setViewData(viewData): Promise<void> {
    super.setViewData(viewData);
    this.setDataOnForm(viewData);
  }

  public setEnableOnDataFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }

  public setErrorInputMessage(shouldShow: boolean): void {
    this.dom.$inputError.Error = shouldShow
      ? this.dependencies.translateFn(
        'Security analysts will not be able to perform rapid incident response actions from GravityZone for this Atlassian Cloud integration. You can enable this setting later by editing the integration.'
      )
      : null;
  }

  private setDataOnForm(viewData): void {
    if (_.get(viewData, ['response', '_id'], false)) {
      const responseAction = _.get(viewData, ['response', 'specifics', 'responseActions'], false);
      this.formGroup.value = {
        org_id: _.get(viewData, ['response', 'configuration', 'org_id'], ''),
        api_token: _.get(viewData, ['response', 'configuration', 'api_token'], ''),
        responseActions: responseAction,
      };
      this.dom.$responseActions.Input.checked = responseAction;
      this.setErrorInputMessage(!responseAction);
    } else {
      this.dom.$responseActions.Input.checked = true;
      this.setErrorInputMessage(false);
    }
  }
}
